import { useCallback, useContext, useMemo } from 'react'

import { Tag } from 'antd'
import { useSelector } from 'react-redux'

import DataTable from '../../../Components/antd/Tables/DataTable/DataTable'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { InstitutionContext } from '../../../Providers/InstitutionProvider'
import { selectUser } from '../../../reducers/UserReducer'
import { InstitutionOptions, OrderBy } from '../../../utils/constants'
import { isCoordinator, isEncoder, isHospitalAdmin, isSchoolUser } from '../../../utils/roles'
import { getters, StudentProperties as _S, Student, StudentStatusTypes } from '../../../utils/entities/student'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { Action, DeleteAction } from '../../../Components/antd/Tables/DataTable/utils/actions'
import { faEdit, faFileArchive, faKey, faUserGraduate } from '@fortawesome/pro-solid-svg-icons'
import { Modes, StudentActionsContext } from '../../../Providers/Student/StudentActions'
import StudentFilters from './StudentFilters'
import { isObject } from '../../../utils'
import { StudentFormModalContext } from '../../../Providers/Forms/StudentForm/StudentFormModal'
import { isArray, isFunction } from 'lodash'
import { Actions } from '../../../utils/events'
import { Permissions } from '../../../Providers/Forms/StudentForm/SearchStudentModalProvider'

export const StatusTagStyles = Object.freeze({
  [StudentStatusTypes.GRADUATED]: { backgroundColor: 'transparent', borderColor: '#ff4d4f', color: '#ff4d4f', borderRadius: '8px', fontWeight: 500 },
  [StudentStatusTypes.CURRENTLY_STUDYING]: { backgroundColor: 'transparent', borderColor: '#52c41a', color: '#52c41a', borderRadius: '8px', fontWeight: 500 },
  [StudentStatusTypes.DROPPED_OUT]: { backgroundColor: 'transparent', borderColor: '#d4b106', color: '#d4b106', borderRadius: '8px', fontWeight: 500 },
  DEFAULT: { backgroundColor: 'transparent', borderColor: '#8c8c8c', color: '#8c8c8c', borderRadius: '8px', fontWeight: 500 }
})
const SMALL_COLUMN = { minWidth: '125px', maxWidth: '175px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }
const MEDIUM_COLUMN = { minWidth: '225px', maxWidth: '225px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }

export const getStudentActiveKeys = user => {
  let keys = [_S.SCHOOL, _S.SECTIONS, _S.LASTNAME, _S.FIRSTNAME, _S.EMAIL, _S.MATRICULE, _S.PHONE_NUMBER, _S.BIRTHDATE, _S.GENDER, _S.NATIONALITY, _S.STATUS]

  if (isHospitalAdmin(user)) {
    keys.push(_S.NISS)
  } else if (isEncoder(user) || isCoordinator(user)) {
    keys.push(_S.NISS)
    keys.push('user.lastLogin')
  }

  if (isSchoolUser(user)) {
    keys = keys.filter(k => _S.SCHOOL !== k)
  }

  return keys
}

const StudentsTable = ({ data, exporter, parameters, loading, onChange, onDelete, onFiltersChange, onKeysChanged, onParametersChange, onResetFilters }) => {
  const { permissions } = useContext(GlobalContext)
  const { institution, options } = useContext(InstitutionContext) ?? {}
  const { setContext } = useContext(StudentActionsContext)
  const { setFormHandler } = useContext(StudentFormModalContext) ?? {}

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const usePublipostage = isObject(options) && options[InstitutionOptions.PUBLIPOSTAGE]
  // A student can be enrolled in several schools: a school user must see the section of *their*
  // school, not the first one returned by the API. Institution users keep the default (first section).
  const userSchool = isSchoolUser(user) && isObject(user.school) ? user.school : null

  const canEditStudent = useCallback(student => {
    const owner = student?.owner ?? student?.user?.owner ?? student?.user?.ownerId
    const ownerType = (student?.ownerType ?? student?.user?.ownerType ?? '').toLowerCase()

    return (owner === institution?.id && ownerType === 'institution') || isEncoder(user) || isCoordinator(user)
  }, [institution?.id])

  const columns = useMemo(() => {
    const activeKeys = getStudentActiveKeys(user)
    const columns = [
      { title: t('common.school'), key: getters.schoolName, style: MEDIUM_COLUMN, id: _S.SCHOOL },
      { title: t('common.section'), orderBy: OrderBy.SECTIONS, key: s => getters.sectionFullAbbreviation(s, userSchool), style: SMALL_COLUMN, id: _S.SECTIONS },
      { title: t('common.lastname'), orderBy: OrderBy.LASTNAME, key: getters.lastName, style: SMALL_COLUMN, id: _S.LASTNAME },
      { title: t('common.firstname'), orderBy: OrderBy.FIRSTNAME, key: getters.firstName, style: SMALL_COLUMN, id: _S.FIRSTNAME },
      { title: t('common.email'), orderBy: OrderBy.EMAIL, key: getters.email, style: MEDIUM_COLUMN, id: _S.EMAIL },
      { title: t('common.matricule'), orderBy: OrderBy.MATRICULE, key: getters.matricule, style: SMALL_COLUMN, id: _S.MATRICULE },
      { title: t('common.phone'), orderBy: OrderBy.PHONE, key: getters.phoneNumber, style: SMALL_COLUMN, id: _S.PHONE_NUMBER },
      { title: t('common.birthdate'), orderBy: OrderBy.BIRTHDATE, key: getters.europeanBirthDate, style: SMALL_COLUMN, id: _S.BIRTHDATE },
      { title: t('common.gender'), key: getters.gender, style: SMALL_COLUMN, id: _S.GENDER },
      {
        title: t('common.nationality'),
        key: s => {
          const nationality = getters.nationality(s)

          return nationality ? t('nationalities.' + nationality.trim().toLowerCase()) : ''
        },
        style: SMALL_COLUMN,
        id: _S.NATIONALITY
      },
      {
        title: t('common.status'),
        key: s => {
          const status = getters.status(s)
          const props = { title: t('student_status_codes.' + (status ?? 'none').toLowerCase()), key: status ?? 'DEFAULT' }

          return <Tag style={StatusTagStyles[props.key]}> {props.title} </Tag>
        },
        id: _S.STATUS
      },
      { title: t('common.niss'), orderBy: OrderBy.NISS, key: getters.niss, style: { minWidth: '200px' }, id: _S.NISS },
      { title: t('common.last_login'), orderBy: OrderBy.LAST_LOGIN, key: getters.lastLogin, id: 'user.lastLogin' }
    ]

    return columns.filter(c => activeKeys.includes(c.id))
  }, [user.id, userSchool?.id, t])

  const getEditionPermission = useCallback((student, currentUser) => {
    let isGranted = Permissions.READ_ONLY

    if (isSchoolUser(currentUser) && isObject(currentUser.school)) {
      if (student.ownerType === 'INSTITUTION') {
        isGranted = Permissions.GOD
      } else if (student.owner === currentUser.school.id) {
        isGranted = Permissions.GOD
      } else {
        const studentOwner = isArray(student.sections) && student.sections.find(s => s.school.id === student.owner)?.school

        isGranted = isObject(studentOwner) && (studentOwner.id === currentUser.school.id || !studentOwner.premium) ? Permissions.GOD : Permissions.EMAIL_AND_MATRICULE_RESTRICTED
      }
    } else if (isHospitalAdmin(currentUser)) {
      if (student.ownerType === 'SCHOOL') {
        isGranted = Permissions.READ_ONLY
      } else {
        isGranted = isArray(student.sections) && !student.sections.some(s => s.school.premium) ? Permissions.MATRICULE_RESTRICTED : Permissions.READ_ONLY
      }
    }

    return isGranted
  }, [])

  const actions = useMemo(() => [
    new Action({
      toRender: () => isFunction(setFormHandler),
      disabled: s => !canEditStudent(s),
      title: 'common.edit',
      icon: faEdit,
      onClick: s => setFormHandler(form => ({
        ...form,
        data: new Student(s),
        mode: getEditionPermission(s, user),
        callback: s => onChange(s, Actions.UPDATE)
      }))
    }),
    new Action({
      title: 'students_table.actions.display_student_sheet',
      icon: faUserGraduate,
      onClick: s => setContext({ data: s, mode: Modes.OPEN_SHEET })
    }),
    new Action({
      // A student whose account is external (institution SSO) has their password managed by that
      // SSO: the backend would answer 409. The button is therefore disabled (defensive guard: with
      // no isExternal field on the profile, the behaviour stays unchanged).
      disabled: s => !permissions.canResetPassword || !!(s.user && s.user.isExternal),
      title: 'students_table.actions.reset_password',
      icon: faKey,
      onClick: s => setContext({ data: s, mode: Modes.RESET_PASSWORD })
    }),
    new Action({
      toRender: () => usePublipostage,
      title: 'students_table.actions.publipostage',
      icon: faFileArchive,
      onClick: s => setContext({ data: s, mode: Modes.PUBLIPOSTAGE })
    }),
    new DeleteAction({
      toRender: () => isFunction(onDelete),
      popconfirmTitle: 'students_table.actions.delete_student',
      onDelete: onDelete
    })
  ], [usePublipostage, permissions.canResetPassword, onDelete, canEditStudent, setFormHandler, onChange, getEditionPermission, user.id, t])

  return (
    <DataTable
      actions={actions}
      columns={columns}
      data={data}
      exporter={exporter}
      filtersMenu={(<StudentFilters filters={parameters.filters} onFiltersChange={onFiltersChange} />)}
      loading={loading}
      metadata={parameters.metadata}
      parameters={parameters}
      onCreate={isFunction(setFormHandler) && isSchoolUser(user) ? () => setFormHandler(form => ({ ...form, data: new Student(), callback: onChange })) : undefined}
      onParametersChange={onParametersChange}
      onKeysChanged={onKeysChanged}
      onResetFilters={onResetFilters}
    />
  )
}

export default StudentsTable
